/**
 * PublicSigningPage (Landlord)
 * Token-authenticated signing ceremony — no login required.
 * Accessed via /sign/:token from the signing request email link.
 *
 * Flow: Consent disclosure -> Signing ceremony -> Completion message
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { recordCeremonyConsent } from '../../lib/api';
import { SigningCeremony } from '../../components/signing/SigningCeremony';
import { EsignConsent } from '../../components/signing/EsignConsent';

export function PublicSigningPage() {
  const { token } = useParams<{ token: string }>();
  const [consented, setConsented] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center" data-testid="public-signing-page">
        <div className="bg-card rounded-lg border border-border p-12 text-center max-w-md">
          <h2 className="text-xl font-semibold text-foreground mb-3">Invalid signing link</h2>
          <p className="text-sm text-muted-foreground">This signing link is not valid. Please check the link in your email.</p>
        </div>
      </div>
    );
  }

  if (complete) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center" data-testid="public-signing-page">
        <div className="bg-card rounded-lg border border-border p-12 text-center max-w-md">
          <div className="w-12 h-12 bg-success/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-success text-xl font-bold" aria-hidden="true">&#10003;</span>
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-3">Signing complete!</h2>
          <p className="text-sm text-muted-foreground">
            Thank you for signing. Your document has been submitted. You may close this page.
          </p>
        </div>
      </div>
    );
  }

  // Consent disclosure — shown before the signing ceremony
  if (!consented) {
    async function handleConsent() {
      try {
        setConsentLoading(true);
        await recordCeremonyConsent(token!);
        setConsented(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to record consent');
      } finally {
        setConsentLoading(false);
      }
    }

    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center" data-testid="public-signing-page">
        <div className="bg-card rounded-lg border border-border p-8 max-w-lg mx-4">
          <h2 className="text-lg font-semibold text-foreground mb-4">Electronic Signature Consent</h2>

          {error && (
            <div className="bg-destructive/10 border border-destructive/25 rounded-md p-3 mb-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <EsignConsent
            onAgree={handleConsent}
            agreeTestId="consent-agree-btn"
            agreePending={consentLoading}
            agreeDisabled={consentLoading}
            agreeButtonClassName="flex-1 min-h-[44px]"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="public-signing-page">
      {/* Full width: the ceremony scales the PDF page to the viewport (no max-width gutter). */}
      <div className="w-full px-4 md:px-6 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive/25 rounded-lg p-4 mb-4">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
        <SigningCeremony
          signingToken={token}
          onComplete={() => setComplete(true)}
          onError={(err) => setError(err)}
        />
      </div>
    </div>
  );
}

export default PublicSigningPage;
